import { DateTime } from 'luxon';

export const TwentyFourHourTimeFormat = 'HH:mm ZZZZ';
export const TwelveHourTimeFormat = 'hh:mm a ZZZZ';
export const DateFormat = 'MM/dd/yy';

export const TwentyFourHourDateTimeFormat = `${DateFormat} ${TwentyFourHourTimeFormat}`;
export const TwelveHourDateTimeFormat = `${DateFormat} ${TwelveHourTimeFormat}`;

/**
 * @description Converts an ISO string to a date time string setting the zone to the system tz or a specified zone.
 * @author Jacob Cutshall
 */
export default function convertIsoToDateTimeString(
  isoString: string,
  options?: {
    twelveHourFormat?: boolean;
    zone?: string;
  },
) {
  const { twelveHourFormat = false, zone = 'system' } = options ? options : {};

  const dateObj = DateTime.fromISO(isoString, { zone: 'utc' }).setZone(zone);

  if (twelveHourFormat) {
    return dateObj.toFormat(TwelveHourDateTimeFormat);
  }

  return dateObj.toFormat(TwentyFourHourDateTimeFormat);
}

/**
 * The function `convertIsoToDate` takes an ISO string and converts it to a formatted date string in
 * the specified time zone.
 * @param {string} isoString - The `isoString` parameter is a string representing a date and time in
 * ISO 8601 format, such as "2022-01-15T08:30:00Z".
 * @param [zone=utc] - The `zone` parameter in the `convertIsoToDate` function specifies the time zone
 * to use when converting the ISO string to a date. By default, it is set to "utc", but you can provide
 * a different time zone if needed.
 * @returns The function `convertIsoToDate` takes an ISO string and an optional time zone parameter,
 * then converts the ISO string to a DateTime object using the specified time zone (default is "utc"),
 * and finally returns the formatted date in the "MM/dd/yy" format.
 */
export function convertIsoToDate(isoString: string, zone = 'utc') {
  // should we set the zone to system before format? I know this is used for subscription dates so mabe it should stay UTC
  return DateTime.fromISO(isoString, { zone }).toFormat('MM/dd/yy');
}

export function convertISOToJSDate(isoString: string) {
  return DateTime.fromISO(isoString, { zone: 'utc' }).toJSDate();
}
export function convertSQLToJSDate(isoString: string) {
  return DateTime.fromSQL(isoString, { zone: 'utc' }).toJSDate();
}

export function convertJSDateToDateTimeString(
  jsDate: Date,
  options?: {
    twelveHourFormat?: boolean;
    zone?: string;
  },
) {
  const { twelveHourFormat = false, zone = 'system' } = options ? options : {};
  const dateTimeObj = DateTime.fromJSDate(jsDate).setZone(zone);

  if (twelveHourFormat) {
    return dateTimeObj.toFormat(TwelveHourDateTimeFormat);
  } else {
    return dateTimeObj.toFormat(TwentyFourHourDateTimeFormat);
  }
}

export function convertDateTimeToString(
  dateTimeObj: DateTime,
  options?: {
    customFormat?: string;
    twelveHourFormat?: boolean;
    zone?: string;
  },
): string {
  const {
    customFormat = undefined,
    twelveHourFormat = false,
    zone = 'system',
  } = options ?? {};

  const format = (() => {
    if (customFormat) {
      return customFormat;
    } else if (twelveHourFormat) {
      return TwelveHourDateTimeFormat;
    } else {
      return TwentyFourHourDateTimeFormat;
    }
  })();

  return dateTimeObj.setZone(zone).toFormat(format);
}

export function convertEpochToDateTimeString(
  epoch: number,
  options?: {
    twelveHourFormat?: boolean;
    shortFormat?: boolean;
    zone?: string;
  },
) {
  const { twelveHourFormat = false, zone = 'system' } = options ? options : {};
  const dateTimeObj = DateTime.fromMillis(epoch).setZone(zone);

  const format = twelveHourFormat
    ? TwelveHourDateTimeFormat
    : TwentyFourHourDateTimeFormat;
  return dateTimeObj.setZone(zone).toFormat(format);
}

export function convertIanaToTimezone(ianaString: string) {
  return DateTime.now().setZone(ianaString).offsetNameShort;
}

export function checkIsAfterToday(
  inputDate: DateTime,
  options: { zone?: string },
) {
  const { zone = 'system' } = options;

  const todayStart = DateTime.now().setZone(zone).startOf('day');
  const inputStart = inputDate.setZone(zone).startOf('day');

  return inputStart > todayStart;
}

/**
 *
 * @returns an array of iso date strings between the max and min dates sorted from max to min
 * @author Syed Saadat
 */
export function generateDateRangeStrings(
  minDate: string | DateTime,
  maxDate: string | DateTime,
  format: string = 'yyyy-MM-dd',
): Array<string> {
  const start =
    typeof minDate === 'string' ? DateTime.fromISO(minDate) : minDate;
  const end = typeof maxDate === 'string' ? DateTime.fromISO(maxDate) : maxDate;

  if (!start.isValid || !end.isValid) {
    return [];
  }

  if (start > end) {
    return [];
  }

  const dateStrings: Array<string> = [];
  const normalizedStart: DateTime = start.startOf('day');
  const normalizedEnd: DateTime = end.startOf('day');

  let currentDate = normalizedEnd;
  while (normalizedStart <= currentDate) {
    dateStrings.push(currentDate.toFormat(format));
    currentDate = currentDate.minus({ days: 1 });
  }

  return dateStrings;
}

/**
 *
 * @param isoDateStrings array of iso format date strings
 * @returns an object containing the max and min dates. these values are
 * evaluated to null if the any string is not a valid format
 * @author Syed Saadat
 */
export function getMinAndMaxIsoDates(isoDateStrings: Array<string>): {
  min: string | null;
  max: string | null;
} {
  if (isoDateStrings.length === 0) {
    return { min: null, max: null };
  }

  const validDates = isoDateStrings
    .map((isoString: string) => {
      return DateTime.fromISO(isoString);
    })
    .filter((dateTime: DateTime): dateTime is DateTime<true> => {
      return dateTime.isValid;
    });

  if (validDates.length === 0) {
    return {
      min: 'Invalid Dates',
      max: 'Invalid Dates',
    };
  }

  const { minDateTime, maxDateTime } = validDates.reduce<{
    minDateTime: DateTime<true>;
    maxDateTime: DateTime<true>;
  }>(
    (acc, currentDateTime) => {
      if (currentDateTime < acc.minDateTime) {
        acc.minDateTime = currentDateTime;
      }

      if (currentDateTime > acc.maxDateTime) {
        acc.maxDateTime = currentDateTime;
      }

      return acc;
    },
    { minDateTime: validDates[0], maxDateTime: validDates[0] },
  );

  return {
    min: minDateTime.toISODate(),
    max: maxDateTime.toISODate(),
  };
}
